/** Loading one API answer into a screen, keyed so moving between records reloads. */
import { useEffect, useState } from "react";
import type { AdminPatientIdentity } from "@lattice/core";
import type { Result } from "./api.ts";

export type Load<T> =
  | { readonly state: "loading" }
  | { readonly state: "ready"; readonly data: T }
  | {
      readonly state: "failed";
      readonly status: number;
      readonly error: string;
      readonly patient: AdminPatientIdentity | null;
    };

/**
 * The answer for `key`, and a setter for when a change returns the updated record. An answer that
 * arrives after the key changed is dropped, so a slow response for the previous record never
 * replaces the one now on screen.
 */
export function useLoad<T>(
  key: string,
  load: () => Promise<Result<T>>,
): readonly [Load<T>, (data: T) => void] {
  const [loaded, setLoaded] = useState<{ readonly key: string; readonly value: Load<T> }>({
    key,
    value: { state: "loading" },
  });

  useEffect(() => {
    let current = true;

    void load().then((result) => {
      if (!current) return;

      setLoaded({
        key,
        value: result.ok
          ? { state: "ready", data: result.data }
          : {
              state: "failed",
              status: result.status,
              error: result.error,
              patient: result.patient,
            },
      });
    });

    return () => {
      current = false;
    };
    /* `load` is a new closure every render, and `key` names everything it reads. */
  }, [key]);

  const value: Load<T> = loaded.key === key ? loaded.value : { state: "loading" };

  return [value, (data: T) => setLoaded({ key, value: { state: "ready", data } })];
}
